/* ============================================
   ChatSidebar — Conversation list panel
   Search, filter tabs, sorted by last message
   ============================================ */
import { useMemo, useState, useEffect, useRef } from 'react'
import { IconSearch, IconWhatsApp, IconMail, IconMessageCircle, IconGlobe, IconPlus, IconUser, IconChevronDown, IconBell, IconBellOff } from '@/components/icons/Icons'
import { useConversacionesSilenciadas } from '@/hooks/useConversacionesSilenciadas'
import { usePendientesChat } from '@/hooks/usePendientesChat'
import { alternarSilencio } from '@/lib/conversaciones-silenciadas'
import { showToast } from '@/components/layout/Toast'
import type { Conversacion, CanalChat, Contacto } from '@/lib/types'
import { fixMojibake } from '@/lib/utils'

interface ChatSidebarProps {
  conversations: Conversacion[]
  contactMap?: Record<number, Contacto>
  originNameMap?: Record<string, string>
  selectedId: number | null
  onSelect: (id: number) => void
  onSearch: (q: string) => void
  filter: string
  onFilterChange: (f: string) => void
  onNewConversation?: () => void
  onViewContact?: (contactoId: number) => void
  loading?: boolean
}

function channelIcon(canal: CanalChat, size = 16) {
  switch (canal) {
    case 'whatsapp': return <IconWhatsApp size={size} />
    case 'email': return <IconMail size={size} />
    case 'web_chat': return <IconGlobe size={size} />
    default: return <IconMessageCircle size={size} />
  }
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const now = Date.now()
  const date = new Date(dateStr).getTime()
  const diff = Math.floor((now - date) / 1000)

  if (diff < 60) return 'ahora'
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`
  return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit' })
}

const FILTERS = [
  { key: 'todas', label: 'Todas' },
  { key: 'no_leidas', label: 'No leidas' },
  { key: 'sin_vincular', label: 'Sin vincular' },
] as const

export default function ChatSidebar({
  conversations,
  contactMap = {},
  originNameMap = {},
  selectedId,
  onSelect,
  onSearch,
  filter,
  onFilterChange,
  onNewConversation,
  onViewContact,
}: ChatSidebarProps) {
  const silenciadas = useConversacionesSilenciadas()
  const pendientes = usePendientesChat()
  const [menuAbierto, setMenuAbierto] = useState<number | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Cerrar el menú al hacer click afuera o con Escape
  useEffect(() => {
    if (menuAbierto === null) return
    function alClickAfuera(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuAbierto(null)
    }
    function alEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuAbierto(null)
    }
    document.addEventListener('mousedown', alClickAfuera)
    document.addEventListener('keydown', alEscape)
    return () => {
      document.removeEventListener('mousedown', alClickAfuera)
      document.removeEventListener('keydown', alEscape)
    }
  }, [menuAbierto])

  async function alternar(id: number, silenciada: boolean) {
    setMenuAbierto(null)
    try {
      await alternarSilencio(id)
      showToast(silenciada ? 'Notificaciones reactivadas' : 'Chat silenciado', 'success')
    } catch {
      showToast('No se pudo guardar la preferencia', 'error')
    }
  }

  const sorted = useMemo(() => {
    let filtered = [...conversations]

    // Apply filter
    if (filter === 'no_leidas') {
      filtered = filtered.filter((c) => c.mensajes_no_leidos > 0)
    } else if (filter === 'sin_vincular') {
      filtered = filtered.filter((c) => c.contacto_id === null)
    }

    // Sort by ultimo_mensaje_at DESC (most recent first)
    filtered.sort((a, b) => {
      const aTime = a.ultimo_mensaje_at ? new Date(a.ultimo_mensaje_at).getTime() : 0
      const bTime = b.ultimo_mensaje_at ? new Date(b.ultimo_mensaje_at).getTime() : 0
      return bTime - aTime
    })

    return filtered
  }, [conversations, filter])

  return (
    <div className="chat-sidebar">
      {/* Header */}
      <div className="chat-sidebar__header">
        <div className="chat-sidebar__header-row">
          <span className="chat-sidebar__title">Conversaciones</span>
          {onNewConversation && (
            <button
              className="btn btn-sm btn-primary chat-sidebar__new-btn"
              onClick={onNewConversation}
              title="Nueva conversacion WhatsApp"
              type="button"
            >
              <IconPlus size={14} /> Nuevo
            </button>
          )}
        </div>
        <div className="chat-sidebar__search">
          <IconSearch size={16} />
          <input
            type="text"
            placeholder="Buscar conversacion..."
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="chat-sidebar__filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`chat-filter-btn ${filter === f.key ? 'chat-filter-btn--active' : ''}`}
            onClick={() => onFilterChange(f.key)}
            type="button"
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Conversation list */}
      <div className="chat-sidebar__list">
        {sorted.length === 0 && (
          <div className="chat-window__empty" style={{ padding: 32 }}>
            <span style={{ fontSize: '0.85rem' }}>Sin conversaciones</span>
          </div>
        )}
        {sorted.map((conv) => {
          // Resolve display name: origin config > contacto name > metadata > chat_id
          const contact = conv.contacto_id ? contactMap[conv.contacto_id] : null
          const contactName = contact ? `${contact.nombre}${contact.apellido ? ' ' + contact.apellido : ''}` : null
          const metaName = (conv.metadata as Record<string, unknown>)?.contact_name as string | undefined
          const originName = originNameMap[`${conv.canal}:${conv.channel_id || 'default'}`]
          const displayName = originName || contactName || metaName || conv.chat_id
          const isSelected = conv.id === selectedId
          const isNew = conv.contacto_id === null
          const silenciada = silenciadas.has(conv.id)

          return (
            <div
              key={conv.id}
              className={`chat-conv-item ${isSelected ? 'chat-conv-item--active' : ''} ${silenciada ? 'chat-conv-item--silenciada' : ''} ${menuAbierto === conv.id ? 'chat-conv-item--menu-abierto' : ''}`}
              onClick={() => onSelect(conv.id)}
            >
              {/* Channel icon */}
              <div className={`chat-conv-item__icon chat-conv-item__icon--${conv.canal}`}>
                {channelIcon(conv.canal, 18)}
              </div>

              {/* Content */}
              <div className="chat-conv-item__body">
                <div className="chat-conv-item__name">
                  {displayName}
                  {contact && onViewContact && (
                    <button
                      className="chat-conv-item__info-btn"
                      onClick={(e) => { e.stopPropagation(); onViewContact(contact.id) }}
                      title="Ver contacto"
                      type="button"
                    >
                      <IconUser size={12} />
                    </button>
                  )}
                </div>
                <div className="chat-conv-item__preview">
                  {conv.ultimo_mensaje ? fixMojibake(conv.ultimo_mensaje) : 'Sin mensajes'}
                </div>
              </div>

              {/* Meta: time + badges */}
              <div className="chat-conv-item__meta">
                <span className="chat-conv-item__time">
                  {silenciada && (
                    <IconBellOff size={12} className="chat-conv-item__muted-icon" />
                  )}
                  {timeAgo(conv.ultimo_mensaje_at)}
                </span>
                {isNew && (
                  <span className="chat-conv-item__new-badge">NUEVO</span>
                )}
                {conv.mensajes_no_leidos > 0 && (
                  <span className={`chat-conv-item__unread ${silenciada ? 'chat-conv-item__unread--silenciada' : ''}`}>
                    {conv.mensajes_no_leidos}
                  </span>
                )}
                {/* Pendientes de hacer, no de leer: un chat puede estar en cero no
                    leídos y tener tres pedidos sin atender. */}
                {(pendientes.get(conv.id) ?? 0) > 0 && (
                  <span
                    className="chat-conv-item__pendientes"
                    title={`${pendientes.get(conv.id)} mensaje(s) que piden algo y siguen sin atender`}
                  >
                    {pendientes.get(conv.id)}
                  </span>
                )}
              </div>

              {/* Menú del chat — por ahora, solo silenciar */}
              <button
                className={`chat-conv-item__menu-btn ${menuAbierto === conv.id ? 'chat-conv-item__menu-btn--abierto' : ''}`}
                onClick={(e) => { e.stopPropagation(); setMenuAbierto(menuAbierto === conv.id ? null : conv.id) }}
                title="Opciones del chat"
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuAbierto === conv.id}
              >
                <IconChevronDown size={14} />
              </button>
              {menuAbierto === conv.id && (
                <div className="chat-conv-item__menu" ref={menuRef} role="menu" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="chat-conv-item__menu-option"
                    onClick={() => alternar(conv.id, silenciada)}
                    type="button"
                    role="menuitem"
                  >
                    {silenciada ? <IconBell size={15} /> : <IconBellOff size={15} />}
                    {silenciada ? 'Reactivar notificaciones' : 'Silenciar notificaciones'}
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
